import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #mobileQuery = window.matchMedia('(max-width: 749px)');
  #backdrop = null;
  #dragPointerId = null;
  #dragStartX = 0;
  #dragStartY = 0;
  #dragOffset = 0;
  #dragWidth = 0;
  #dragging = false;
  #dragDirectionResolved = false;
  #suppressNextClick = false;

  connectedCallback() {
    super.connectedCallback();

    this.#backdrop = this.querySelector('.menu-drawer__backdrop');

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onClickCapture, true);

    this.refs.menuDrawer.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.menuDrawer.addEventListener('pointermove', this.#onPointerMove);
    this.refs.menuDrawer.addEventListener('pointerup', this.#onPointerUp);
    this.refs.menuDrawer.addEventListener('pointercancel', this.#onPointerCancel);

    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onClickCapture, true);

    this.refs.menuDrawer.removeEventListener('pointerdown', this.#onPointerDown);
    this.refs.menuDrawer.removeEventListener('pointermove', this.#onPointerMove);
    this.refs.menuDrawer.removeEventListener('pointerup', this.#onPointerUp);
    this.refs.menuDrawer.removeEventListener('pointercancel', this.#onPointerCancel);
  }

  /**
   * Close the main menu drawer when Escape is pressed.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Prevent the click generated after a completed horizontal drag.
   *
   * @param {MouseEvent} event
   */
  #onClickCapture = (event) => {
    if (!this.#suppressNextClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#suppressNextClick = false;
  };

  /**
   * @returns {boolean}
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

      onAnimationEnd(drawer || details, () => trapFocus(details), {
        subtree: false,
      });
    });
  }

  /**
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails = this.querySelectorAll(
            'details[open]:not(accordion-custom > details)'
          );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      {
        subtree: false,
      }
    );
  }

  /**
   * Start mobile drawer gesture tracking.
   *
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (!this.#mobileQuery.matches) return;
    if (!this.isOpen) return;
    if (!event.isPrimary) return;
    if (event.pointerType === 'mouse') return;

    if (
      this.refs.menuDrawer.classList.contains(
        'menu-drawer--has-submenu-opened'
      )
    ) {
      return;
    }

    if (
      event.target instanceof Element &&
      event.target.closest('input, textarea, select')
    ) {
      return;
    }

    this.#dragPointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartY = event.clientY;
    this.#dragOffset = 0;
    this.#dragWidth = this.refs.menuDrawer.getBoundingClientRect().width;
    this.#dragging = false;
    this.#dragDirectionResolved = false;

    this.refs.menuDrawer.setPointerCapture?.(event.pointerId);
  };

  /**
   * Follow horizontal finger movement.
   *
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (event.pointerId !== this.#dragPointerId) return;

    const deltaX = event.clientX - this.#dragStartX;
    const deltaY = event.clientY - this.#dragStartY;
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);

    if (!this.#dragDirectionResolved) {
      if (Math.max(absoluteX, absoluteY) < 8) return;

      this.#dragDirectionResolved = true;

      if (absoluteY >= absoluteX) {
        this.#releasePointer(event.pointerId);
        this.#resetDragTracking();
        return;
      }

      this.#dragging = true;
      this.refs.menuDrawer.classList.add('menu-drawer--dragging');
    }

    if (!this.#dragging) return;

    event.preventDefault();

    this.#dragOffset = Math.max(
      -this.#dragWidth,
      Math.min(0, deltaX)
    );

    this.refs.menuDrawer.style.setProperty(
      '--menu-drawer-drag-x',
      `${this.#dragOffset}px`
    );

    const openProgress =
      1 - Math.abs(this.#dragOffset) / this.#dragWidth;

    if (this.#backdrop instanceof HTMLElement) {
      this.#backdrop.style.opacity = String(
        Math.max(0, Math.min(1, openProgress))
      );
    }
  };

  /**
   * Snap open or closed when the finger is released.
   *
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (event.pointerId !== this.#dragPointerId) return;

    const wasDragging = this.#dragging;
    const draggedDistance = Math.abs(this.#dragOffset);
    const shouldClose =
      wasDragging && draggedDistance > this.#dragWidth / 2;

    if (wasDragging && draggedDistance > 6) {
      this.#suppressNextClick = true;

      window.setTimeout(() => {
        this.#suppressNextClick = false;
      }, 120);
    }

    this.#releasePointer(event.pointerId);

    if (!wasDragging) {
      this.#resetDragTracking();
      return;
    }

    this.refs.menuDrawer.classList.remove('menu-drawer--dragging');

    requestAnimationFrame(() => {
      this.refs.menuDrawer.style.removeProperty(
        '--menu-drawer-drag-x'
      );

      if (this.#backdrop instanceof HTMLElement) {
        this.#backdrop.style.removeProperty('opacity');
      }

      if (shouldClose) {
        this.close();
      }
    });

    this.#resetDragTracking();
  };

  /**
   * Restore the drawer if the browser cancels the pointer gesture.
   *
   * @param {PointerEvent} event
   */
  #onPointerCancel = (event) => {
    if (event.pointerId !== this.#dragPointerId) return;

    const wasDragging = this.#dragging;

    this.#releasePointer(event.pointerId);

    if (wasDragging) {
      this.refs.menuDrawer.classList.remove('menu-drawer--dragging');

      requestAnimationFrame(() => {
        this.refs.menuDrawer.style.removeProperty(
          '--menu-drawer-drag-x'
        );

        if (this.#backdrop instanceof HTMLElement) {
          this.#backdrop.style.removeProperty('opacity');
        }
      });
    }

    this.#resetDragTracking();
  };

  /**
   * @param {number} pointerId
   */
  #releasePointer(pointerId) {
    if (this.refs.menuDrawer.hasPointerCapture?.(pointerId)) {
      this.refs.menuDrawer.releasePointerCapture(pointerId);
    }
  }

  #resetDragTracking() {
    this.#dragPointerId = null;
    this.#dragStartX = 0;
    this.#dragStartY = 0;
    this.#dragOffset = 0;
    this.#dragWidth = 0;
    this.#dragging = false;
    this.#dragDirectionResolved = false;
  }

  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll(
      '.menu-drawer__animated-element'
    );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      'accordion-custom .details-content'
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}
